import { api } from "@/lib/api";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  ApiResponse,
  Budget,
  BudgetCategory,
  BudgetSubCategory,
  BudgetSummary,
} from "@/lib/types";

export const useGetBudgets = (
  options?: Omit<UseQueryOptions<ApiResponse<Budget[]>>, "queryKey" | "queryFn">
) => {
  return useQuery<ApiResponse<Budget[]>>({
    queryKey: ["budgets"],
    queryFn: async () => {
      const response = await api.get("/budgets");
      return response.data;
    },
    ...options,
  });
};

export const useGetBudgetById = (
  budgetId?: string,
  options?: Omit<UseQueryOptions<ApiResponse<Budget>>, "queryKey" | "queryFn">
) => {
  return useQuery<ApiResponse<Budget>>({
    queryKey: ["budget-detail", budgetId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Budget>>(
        `/budgets/${budgetId}`
      );
      return response.data;
    },
    enabled: !!budgetId,
    ...options,
  });
};

export const useGetBudgetsByVesselId = (
  vesselId?: string,
  options?: Omit<UseQueryOptions<ApiResponse<Budget[]>>, "queryKey" | "queryFn">
) => {
  return useQuery<ApiResponse<Budget[]>>({
    queryKey: ["budgets-by-vessel-id", vesselId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Budget[]>>(
        `/budgets/by-vessel/${vesselId}`
      );
      return response.data;
    },
    enabled: !!vesselId,
    ...options,
  });
};
export const useGetBudgetCategories = (
  budgetId: string,
  options?: Omit<
    UseQueryOptions<ApiResponse<BudgetCategory[]>>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<ApiResponse<BudgetCategory[]>>({
    queryKey: ["budget-categories", budgetId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<BudgetCategory[]>>(
        `/budgets/${budgetId}/categories`
      );
      return response.data;
    },
    enabled: !!budgetId,
    ...options,
  });
};

interface GetBudgetCategoriesByIdProps {
  budgetId: string;
  id: string;
}

export const useGetBudgetCategoriesById = (
  { budgetId, id }: GetBudgetCategoriesByIdProps,
  options?: Omit<
    UseQueryOptions<ApiResponse<BudgetCategory[]>>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<ApiResponse<BudgetCategory[]>>({
    queryKey: ["budget-category-detail", budgetId, id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<BudgetCategory[]>>(
        `/budgets/${budgetId}/categories/${id}`
      );
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

interface GetBudgetSubCategoriesByIdProps {
  id: string;
}

export const useGetBudgetSubCategories = (
  { id }: GetBudgetSubCategoriesByIdProps,
  options?: Omit<
    UseQueryOptions<ApiResponse<BudgetSubCategory[]>>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<ApiResponse<BudgetSubCategory[]>>({
    queryKey: ["budget-sub-categories", id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<BudgetSubCategory[]>>(
        `/budgets/categories/${id}/subcategories`
      );
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

interface GetBudgetSubCategoryByIdProps {
  id: string;
}

export const useGetBudgetSubCategoryById = (
  { id }: GetBudgetSubCategoryByIdProps,
  options?: Omit<
    UseQueryOptions<ApiResponse<BudgetSubCategory>>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<ApiResponse<BudgetSubCategory>>({
    queryKey: ["budget-sub-category-detail", id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<BudgetSubCategory>>(
        `/budgets/subcategories/${id}`
      );
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

interface GetBudgetCategoryByIdProps {
  id: string;
}

export const useGetBudgetCategoryById = (
  { id }: GetBudgetCategoryByIdProps,
  options?: Omit<
    UseQueryOptions<ApiResponse<BudgetCategory>>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<ApiResponse<BudgetCategory>>({
    queryKey: ["budget-category-by-id", id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<BudgetCategory>>(
        `/budgets/categories/${id}`
      );
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useGetBudgetSummaries = (
  options?: Omit<
    UseQueryOptions<ApiResponse<BudgetSummary[]>>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<ApiResponse<BudgetSummary[]>>({
    queryKey: ["budget-summaries"],
    queryFn: async () => {
      const response =
        await api.get<ApiResponse<BudgetSummary[]>>(`/budgets/summaries`);
      return response.data;
    },
    ...options,
  });
};
